<template>
  <simple-edit-base
    ref="simpleEditBase"
    v-bind="$data"
    :filter-category="filterCategory"
    :min-hosts-and-reason="minHostAndReason"
    :get-cycle-disable-info="getCycleDisableInfo"
    @field-change="handleFieldChange"
    @switch-plan="handleSwitchPlan"
  >
    <template #license-before>
      <subscription-switch @switch-plan="handleSwitchOldPlan" />
    </template>
    <template #cycle-after v-if="showAnnualTermMonthly">
      <p class="opc-cycle-tips">{{ $t("billing.payment_tips_for_annual_billed_monthly") }}</p>
    </template>
    <template #footer>
      <p class="bottom-msg-row" v-if="showAddAnotherMeetingPlanButton"
         v-html="$t('billing.add_another_workplace_plan', [addAnotherMeetingLink, addAnotherMeetingPlanLink])"></p>
    </template>
  </simple-edit-base>
</template>

<script>
import SimpleEditBase from "@/components/simple-edit/base.vue";
import logo from "@/assets/plans/icon_ZM.svg";
import {
  planTypes,
  DEFAULT_MAX_HOST,
  ZOOM_MEETING_PRO_MIN_LICENSE,
  BILLING_CYCLE,
  limitBillingCycleWithBaseZoneTypes,
  OPC_HREF_SYNC_QUERY_PARAMS,
} from "@/common/constants";
import { mapGetters } from "vuex";
import { get, groupBy } from "lodash";
import { pageConfig } from "@/common/service/page-config";
import { updateHrefWithQueryParams } from "@/utils";
import ZoomMeetingDialogUtils from "../dialog/utils";
import promoModifyMixin from "@/common/mixin/promo-modify-mixin";
import { mapActions } from "vuex";
import SubscriptionSwitch from '../components/subscription-switch.vue';
export default {
  name: "ZoomMeetingEditSimple",
  components: {
    SimpleEditBase,
    SubscriptionSwitch,
  },
  mixins: [promoModifyMixin],
  data() {
    return {
      planType: planTypes.ZOOM_MEETING,
      logo,
      showSwitchPlanButton: true,
      licensesTooltip: this.$t('billing.zone.attendee_info_edit'),
      licensesMax: DEFAULT_MAX_HOST,
      mounted: false,
    };
  },
  computed: {
    ...mapGetters([
      "getSelectedPlans",
      "currentEditPlan",
      "limitLicenseWithBaseZoneTypes",
      "getUserCurrentSubscriptions",
      "migratePlanState",
      "boughtPlans",
      "basicConfig",
      "disableMonthly2AnnualTip",
      "isMultipleCoupon",
      "planMapFlat",
      "oldPlanCode",
      "multipleSubscriptionScope",
      "msqFeatureEnabled"
    ]),
    selectedPlan() {
      if (!this.mounted) return {};
      return this.$refs.simpleEditBase?.selectedPlan;
    },
    // Whether the account is already MSQ or will become MSQ after checkout.
    // - multiSubscriptionEnabled: backend flag indicating the account already has MSQ
    // - length > 1: multiple paid ZOOM_MEETING subscriptions exist, checkout will make it MSQ
    isMsqAccountOrBecoming() {
      return this.basicConfig.multiSubscriptionEnabled ||
      this.getUserCurrentSubscriptions.filter(p => p.type === planTypes.ZOOM_MEETING && !p.isFreeTrial && !p.isOnlineFreeTrial).length > 1;
    },
    filterCategory() {
      return [this.selectedPlan?.category];
    },
    trackingPlanName() {
      return this.$t(`Zoom One ${this.selectedPlan?.category}`);
    },
    boughtMeetingPlan() {
      //pro increase, plan_code may change
      return this.boughtPlans.find((item) => item.plan_code === this.selectedPlan.plan_code) ?? this.currentEditPlan;
    },
    zmUnitedPlanQuantity() {
      return this.boughtPlans
        .filter((item) => item.type === planTypes.ZOOM_UNITED)
        .reduce((pre, item) => item.cartInfo.selected_number + pre, 0);
    },
    zoneBundlePlanQuantity() {
      return this.boughtPlans
        .filter((item) => item.type === planTypes.ZOOM_ONE_BUNDLE)
        .reduce((pre, item) => item.cartInfo.selected_number + pre, 0);
    },
    maxLicenseAddon() {
      return this.getUserCurrentSubscriptions
        .filter((item) => this.limitLicenseWithBaseZoneTypes?.includes(item.type))
        .reduce((maxPlan, plan) => {
          if (plan.cartInfo.selected_number > get(maxPlan, "cartInfo.selected_number", 0)) {
            return plan;
          }
          return maxPlan;
        }, null);
    },
    minHostAndReason() {
      const cases = [];
      const boughtLicense = get(this.boughtMeetingPlan, "cartInfo.selected_number", 1);

      // Case 1: promote campaign link (partner/marketing coupon)
      if (this.eligibleInfoForPromoCase?.min) {
        const reason = this.eligibleInfoForPromoCase?.reason || "";
        cases.push({
          value: boughtLicense,
          reason,
        });
      }

      // Case 2: meeting plan license can't less than addon plan
      const meetingMin = get(this.selectedPlan, "min", 1);
      const bundleLicense = this.zmUnitedPlanQuantity + this.zoneBundlePlanQuantity;
      const boughtMeetingPlans = this.boughtPlans.filter((item) => item.type === planTypes.ZOOM_MEETING);
      let otherMeetingLicense = 0;
      if (boughtMeetingPlans.length > 1) {
        otherMeetingLicense = boughtMeetingPlans
          .filter((item) => item.plan_code !== pageConfig.oldPlanCode)
          .reduce((sum, item) => sum + get(item, "cartInfo.selected_number", 0), 0);
      }
      let minHost = Math.max(meetingMin - bundleLicense - otherMeetingLicense, 1);

      const maxLicenseAddonLicense = get(this.maxLicenseAddon, "cartInfo.selected_number", 0);
      const serviceMinLicense = maxLicenseAddonLicense - bundleLicense - otherMeetingLicense;

      if (this.maxLicenseAddon && minHost < serviceMinLicense) {
        const maxLicenseAddonTypeNameKey = this.maxLicenseAddon.typeNameKey;
        const maxLicenseAddonTypeName = this.$t(maxLicenseAddonTypeNameKey);
        cases.push({
          value: Math.min(serviceMinLicense, boughtLicense),
          reason: this.$t("billing.meeting_license_decrease_tips_need_decrease_addon", {
            product: maxLicenseAddonTypeName,
          }),
        });
      }

      // Case 3: pro2biz
      if (this.migratePlanState?.is?.pro2biz) {
        const pro2bizMinHost = Math.max(this.boughtMeetingPlan?.cartInfo?.selected_number || 0, ZOOM_MEETING_PRO_MIN_LICENSE);
        cases.push({
          value: pro2bizMinHost,
          reason: "",
        });
      }

      // Compare all cases and return the one with maximum value
      if (cases.length > 0) {
        return cases.reduce((max, current) => current.value > max.value ? current : max);
      }

      return null;
    },
    zonePlans() {
      return this.planMapFlat?.filter((item) => item.type === planTypes.ZOOM_MEETING) || [];
    },
    zonePlanGroupByCategory() {
      return groupBy(this.zonePlans, "category");
    },
    currentPlans() {
      return this.zonePlanGroupByCategory[this.selectedPlan?.category] || [];
    },
    currentFilterPlans() {
      return this.currentPlans.filter(
        (item) =>
          !this.boughtMeetingPlans.some(
            (boughtPlan) =>
              boughtPlan.billing_cycle === item.billing_cycle &&
              boughtPlan.plan_code !== pageConfig.oldPlanCode,
          ),
      );
    },
    boughtMeetingPlans() {
      return this.boughtPlans.filter((item) => item.type === planTypes.ZOOM_MEETING);
    },
    limitCycleAddonPlans() {
      const disableCycle = this.currentPlans.find(
        (p) => p.cycleValue < this.boughtMeetingPlan.cycleValue,
      )?.cycleValue;
      if (!disableCycle) {
        return [];
      }

      const newLimit = [];
      return this.getUserCurrentSubscriptions.filter(
        (item) =>
          limitBillingCycleWithBaseZoneTypes.concat(newLimit).includes(item.type) &&
          item.cycleValue > disableCycle,
      );
    },
    cycleDisabledKeyAndReasonForPartner() {
      return this.getCycleDisabledKeyAndReasonForPartner?.() || null;
    },
    cycleDisabledKeyAndReason() {
      const currentBillingCycles = this.currentFilterPlans.map((p) => p.billing_cycle);
      const currentCycle = this.$refs.simpleEditBase?.formData?.cycle || this.selectedPlan?.billing_cycle;

      const { isBulkLicenseAccount } = this.basicConfig || {};
      if (isBulkLicenseAccount) {
        return {
          key:
            currentCycle === BILLING_CYCLE.MONTHLY
              ? BILLING_CYCLE.ANNUAL
              : BILLING_CYCLE.MONTHLY,
          keys: currentBillingCycles.filter(
            (billingCycle) => billingCycle !== currentCycle,
          ),
          reason: this.$t("billing.error.buld_acount_license"),
        };
      }

      if (this.disableMonthly2AnnualTip && !this.isMultipleCoupon) {
        return {
          key: BILLING_CYCLE.ANNUAL,
          keys: currentBillingCycles.filter(
            (billingCycle) => billingCycle !== currentCycle,
          ),
          reason: this.disableMonthly2AnnualTip,
        };
      }

      if (this.cycleDisabledKeyAndReasonForPartner) {
        return this.cycleDisabledKeyAndReasonForPartner;
      }

      if (this.eligibleInfoForPromoCase?.key) {
        return this.eligibleInfoForPromoCase;
      }

      const planGroupByType = groupBy(this.limitCycleAddonPlans, "type");
      const typeNames = Object.keys(planGroupByType)
        .map((type) => {
          const typeNameKey = get(planGroupByType[type], "0.typeNameKey", "");
          return this.$t(typeNameKey);
        })
        .filter(Boolean);
      const hasMonthlyMeeting = this.boughtMeetingPlans.some(item => item.billing_cycle === BILLING_CYCLE.MONTHLY);
      if (this.limitCycleAddonPlans.length && !hasMonthlyMeeting) {
        const disablePlan = this.currentPlans.find(
          (p) => p.cycleValue < this.boughtMeetingPlan.cycleValue,
        );
        if (disablePlan) {
          return {
            key: disablePlan.billing_cycle,
            reason: this.$t("billing.planform.can_not_select_with_special_addon", [
              this.$t("billing.planform.title.zoom_meeting_zoomonenew"),
              typeNames.join(","),
              this.$t(`billing.billing_cycle.${disablePlan.billing_cycle}`)?.toLowerCase(),
            ]),
          };
        }
      }
      return {};
    },

    addAnotherMeetingLink() {
      if (this.$context.is.modal) {
        const link = `/opc/buy/add?type=add&subPlan=newsubs&from=${this.currentEditPlan?.category}&pageType=modal${this.$context?.is?.simplify ? '&fromSimplifyPage=true' : ''}`;
        return updateHrefWithQueryParams(link, OPC_HREF_SYNC_QUERY_PARAMS);
      }
      return `/opc/buy/add?type=add&subPlan=newsubs&from=${this.currentEditPlan?.category}`;
    },
    canAddCycles() {
      const cycles = this.boughtMeetingPlans.map(item => item.billing_cycle);
      const supportCycles = [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL];
      const hasUnsupportedCycle = cycles.some(cycle => !supportCycles.includes(cycle));
      if (hasUnsupportedCycle) return [];
      return supportCycles.filter(cycle => !cycles.includes(cycle));
    },
    addAnotherMeetingPlanLink() {
      const [cycle] = this.canAddCycles;
      if (cycle === BILLING_CYCLE.MONTHLY) return this.$t('billing.add_monthly_plan_link');
      if (cycle === BILLING_CYCLE.ANNUAL) return this.$t('billing.add_annual_plan_link');
      return '';
    },
    supportMSQ() {
      if (!this.currentEditPlan?.category) return false;
      return this.multipleSubscriptionScope.some(scope => (
        scope.type === planTypes.ZOOM_MEETING && scope.category === this.currentEditPlan?.category
      ));
    },
    showAddAnotherMeetingPlanButton() {
      return (
        this.canAddCycles.length > 0 &&
        !this.migratePlanState?.is?.pro2biz &&
        !this.migratePlanState?.is?.biz2pro &&
        this.msqFeatureEnabled &&
        this.supportMSQ
      );
    },
    showAnnualTermMonthly() {
      return (
        this.mounted && 
        this.$refs.simpleEditBase?.showAnnualTermMonthly &&
        !this.isMsqAccountOrBecoming
      );
    },
  },
  mounted() {
    this.mounted = true;
    ZoomMeetingDialogUtils.install(this.selectedPlan);
  },
  methods: {
    ...mapActions(['switchSelectPlan']),
    getCycleDisableInfo({ billing_cycle }) {
      const cycleDisabledInfo = this.cycleDisabledKeyAndReason;
      const hasCycleRestriction = cycleDisabledInfo && (cycleDisabledInfo.key || cycleDisabledInfo.keys);
      const isMsq = this.isMsqAccountOrBecoming;

      if (!hasCycleRestriction && !isMsq) {
        return null;
      }

      let disabled = false;
      let reason = '';

      // MSQ accounts can only purchase standard annual and monthly cycles
      if (isMsq) {
        disabled = ![BILLING_CYCLE.ANNUAL, BILLING_CYCLE.MONTHLY].includes(billing_cycle);
      }

      // Cycle-specific restrictions (e.g., partner rules, promo cases) are additive
      if (hasCycleRestriction) {
        const disabledByCycleRule = Array.isArray(cycleDisabledInfo.keys)
          ? cycleDisabledInfo.keys.includes(billing_cycle)
          : cycleDisabledInfo.key === billing_cycle;
        if (disabledByCycleRule) {
          disabled = true;
          reason = cycleDisabledInfo.reason || '';
        }
      }

      return {
        disabled,
        reason: disabled ? reason : '',
      };
    },
    handleFieldChange() {
    },
    async showFullComparisonDialog() {
      this.$tracking.buyInteraction("product details", "full plan comparison - impression");
      await ZoomMeetingDialogUtils.showComparisonDialog({
        planCategory: this.selectedPlan.category,
        largeMeetingConfig: {
          school: {
            callback: () => {
            },
            disabled: true,
            display: false,
          },
          schoolPlus: {
            callback: () => {
            },
            disabled: true,
            display: false,
          },
        },
      });
    },
    async handleSwitchPlan() {
      this.$tracking.buyInteraction("explore other plans", `${this.trackingPlanName} - explore other plans`);
      await this.showFullComparisonDialog();
    },
    handleSwitchOldPlan({ billingCycle, licenses }) {
      this.$nextTick(() => {
        this.$refs.simpleEditBase.formData.cycle = billingCycle;
        this.$refs.simpleEditBase.formData.licenses = licenses;
      });
    },
  }
};
</script>
<style lang="scss" scoped>
.opc-cycle-tips {
  color: #686F79;
  font-size: 12px;
}
</style>
